import { useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  SectionCard,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@/components/ui';
import { friendlyMessage } from '@/lib/errors';
import { ApiClientError } from '@/lib/http-core';
import { useHasRole } from '@/providers/AuthProvider';
import {
  CASH_RECEIPT_WRITE_ROLES,
  useOrderCashReceiptMutations,
  useOrderCashReceiptQuery,
  type CashReceiptIdentityType,
  type CashReceiptItem,
  type CashReceiptType,
} from '../hooks/useOrderCashReceipt';

/*
 * 주문 상세 > 현금영수증 — 신청 정보·발급 이력 조회, 실패 건 재발급, 정보 변경 후 발급, 취소(부분 취소).
 * 신청·이력이 없고 대상 주문도 아니면 카드를 숨긴다. BE 배포 전(404)·조회 권한 없음(403)도 숨긴다.
 */

type BadgeVariant = 'neutral' | 'pending' | 'success' | 'danger' | 'progress';

const KST = new Intl.DateTimeFormat('ko-KR', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});
const formatDate = (iso: string | null) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : KST.format(d);
};
const won = (n: number) => `₩${n.toLocaleString('ko-KR')}`;

/** 409 사유(입금확인 전, 살아 있는 발급분 등)는 BE 문구를 그대로 — friendlyMessage 는 COMMON_CONFLICT 를 일반 문구로 바꾼다. */
const reasonOf = (e: unknown, fallback: string): string =>
  e instanceof ApiClientError && e.message && /[가-힣]/.test(e.message) ? e.message : friendlyMessage(e, fallback);

export const OrderCashReceiptSection = ({ orderId }: { orderId: string }) => {
  const { t } = useTranslation('orders');
  const query = useOrderCashReceiptQuery(orderId);
  const { issue, cancel } = useOrderCashReceiptMutations(orderId);
  const canWrite = useHasRole(CASH_RECEIPT_WRITE_ROLES);

  const [issueOpen, setIssueOpen] = useState(false);
  const [form, setForm] = useState<{ receiptType: CashReceiptType; identityType: CashReceiptIdentityType; identityNumber: string }>({
    receiptType: 'INCOME_DEDUCTION',
    identityType: 'PHONE',
    identityNumber: '',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelAmount, setCancelAmount] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelError, setCancelError] = useState<string | null>(null);

  const statusLabel = (s: string) =>
    ({
      WAITING_DEPOSIT: t('orders.cashReceipt.status.WAITING_DEPOSIT', '입금대기'),
      REQUESTED: t('orders.cashReceipt.status.IN_PROGRESS', '발급중'),
      IN_PROGRESS: t('orders.cashReceipt.status.IN_PROGRESS', '발급중'),
      ISSUED: t('orders.cashReceipt.status.ISSUED', '발급완료'),
      FAILED: t('orders.cashReceipt.status.FAILED', '실패'),
      CANCELED: t('orders.cashReceipt.status.CANCELED', '취소'),
    })[s] ?? s;
  const statusVariant = (s: string): BadgeVariant =>
    (({ WAITING_DEPOSIT: 'pending', REQUESTED: 'progress', IN_PROGRESS: 'progress', ISSUED: 'success', FAILED: 'danger', CANCELED: 'neutral' }) as Record<string, BadgeVariant>)[s] ?? 'neutral';
  const typeLabel = (v: string | null) =>
    v === 'INCOME_DEDUCTION'
      ? t('orders.cashReceipt.type.INCOME_DEDUCTION', '소득공제')
      : v === 'EXPENSE_PROOF'
        ? t('orders.cashReceipt.type.EXPENSE_PROOF', '지출증빙')
        : v ?? '-';
  const identityLabel = (v: string) =>
    v === 'BUSINESS_NO' ? t('orders.cashReceipt.identity.BUSINESS_NO', '사업자등록번호') : t('orders.cashReceipt.identity.PHONE', '휴대폰번호');
  const requestSourceLabel = (v: string | null) =>
    ({ CHECKOUT: t('orders.cashReceipt.requestSource.CHECKOUT', '주문서'), MYPAGE: t('orders.cashReceipt.requestSource.MYPAGE', '마이페이지'), ADMIN: t('orders.cashReceipt.requestSource.ADMIN', '관리자') })[v ?? ''] ?? v ?? '-';
  const receiptSourceLabel = (v: string) =>
    ({ API: t('orders.cashReceipt.receiptSource.API', '쇼핑몰 발급'), PG_WINDOW: t('orders.cashReceipt.receiptSource.PG_WINDOW', '결제창(Toss)'), LEGACY: t('orders.cashReceipt.receiptSource.LEGACY', '고도몰 이관') })[v] ?? v;

  const status = query.error?.status;
  if (query.isPending || status === 404 || status === 403) return null;

  if (query.isError) {
    return (
      <SectionCard title={t('orders.cashReceipt.title', '현금영수증')}>
        <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>
          {friendlyMessage(query.error, t('orders.cashReceipt.loadError', '현금영수증 정보를 불러오지 못했습니다.'))}
        </p>
      </SectionCard>
    );
  }

  const data = query.data;
  if (!data.eligible && !data.request && data.receipts.length === 0) return null;

  const req = data.request;
  // 취소 대상 — 쇼핑몰 발급분 중 발급완료 + 잔액 있음(고도몰 이관·결제창은 여기서 취소하지 않는다).
  const cancellable = data.receipts.find((r) => r.source === 'API' && r.status === 'ISSUED' && r.remainingAmount > 0);
  const canReissue = !!req && (req.issueStatus === 'FAILED' || req.issueStatus === 'CANCELED');
  const busy = issue.isPending || cancel.isPending;

  const runIssue = (input: Parameters<typeof issue.mutate>[0], onDone?: () => void) =>
    issue.mutate(input, {
      onSuccess: () => {
        toast.success(t('orders.cashReceipt.toast.issued', '현금영수증 발급을 요청했습니다.'));
        onDone?.();
      },
      onError: (e) => {
        const message = reasonOf(e, t('orders.cashReceipt.toast.issueFail', '현금영수증 발급에 실패했습니다.'));
        if (input) setFormError(message);
        else toast.error(message);
      },
    });

  const submitIssue = () => {
    setFormError(null);
    const digits = form.identityNumber.replace(/\D/g, '');
    if (form.receiptType === 'INCOME_DEDUCTION' && form.identityType !== 'PHONE') {
      return setFormError(t('orders.cashReceipt.error.incomeNeedsPhone', '소득공제는 휴대폰번호로만 발급할 수 있습니다.'));
    }
    if (form.identityType === 'PHONE' ? digits.length < 10 || digits.length > 11 : digits.length !== 10) {
      return setFormError(
        form.identityType === 'PHONE'
          ? t('orders.cashReceipt.error.phone', '휴대폰번호를 확인해 주세요.')
          : t('orders.cashReceipt.error.businessNo', '사업자등록번호 10자리를 확인해 주세요.'),
      );
    }
    runIssue({ ...form, identityNumber: form.identityNumber.trim() }, () => setIssueOpen(false));
  };

  const submitCancel = () => {
    setCancelError(null);
    const trimmed = cancelAmount.replace(/[,\s]/g, '');
    if (trimmed !== '' && (!/^\d+$/.test(trimmed) || Number(trimmed) <= 0 || (cancellable && Number(trimmed) > cancellable.remainingAmount))) {
      return setCancelError(t('orders.cashReceipt.error.amount', '취소 금액은 잔액 이하의 숫자로 입력해 주세요.'));
    }
    const reason = cancelReason.trim();
    cancel.mutate(
      { ...(trimmed !== '' ? { amount: Number(trimmed) } : {}), ...(reason ? { reason } : {}) },
      {
        onSuccess: () => {
          toast.success(t('orders.cashReceipt.toast.canceled', '현금영수증을 취소했습니다.'));
          setCancelOpen(false);
        },
        onError: (e) => setCancelError(reasonOf(e, t('orders.cashReceipt.toast.cancelFail', '현금영수증 취소에 실패했습니다.'))),
      },
    );
  };

  return (
    <SectionCard
      title={t('orders.cashReceipt.title', '현금영수증')}
      aside={req ? <Badge variant={statusVariant(req.issueStatus)} data-testid="cash-receipt-status">{statusLabel(req.issueStatus)}</Badge> : undefined}
      data-testid="order-cash-receipt"
    >
      <div className="flex flex-col gap-3 text-sm">
        {req ? (
          <dl className="grid grid-cols-[110px_1fr] gap-x-2 gap-y-1" data-testid="cash-receipt-request">
            <dt className="text-[color:var(--content-muted)]">{t('orders.cashReceipt.receiptType', '발급 유형')}</dt>
            <dd>{typeLabel(req.receiptType)}</dd>
            <dt className="text-[color:var(--content-muted)]">{identityLabel(req.identityType)}</dt>
            <dd className="tabular-nums">{req.identityMasked ?? '-'}</dd>
            <dt className="text-[color:var(--content-muted)]">{t('orders.cashReceipt.requestSourceLabel', '신청 경로')}</dt>
            <dd>{requestSourceLabel(req.source)} · {formatDate(req.requestedAt)}</dd>
            {req.issueStatus === 'FAILED' && req.lastFailureMessage ? (
              <>
                <dt className="text-[color:var(--content-muted)]">{t('orders.cashReceipt.failure', '실패 사유')}</dt>
                <dd style={{ color: 'var(--status-danger-fg)' }} data-testid="cash-receipt-failure">{req.lastFailureMessage}</dd>
              </>
            ) : null}
            {req.issueStatus === 'WAITING_DEPOSIT' ? (
              <dd className="col-span-2 text-[12px] text-[color:var(--content-muted)]">
                {t('orders.cashReceipt.waitingHint', '입금확인 시 자동으로 발급됩니다.')}
              </dd>
            ) : null}
          </dl>
        ) : (
          <p className="text-[color:var(--content-muted)]">{t('orders.cashReceipt.noRequest', '신청 정보가 없습니다.')}</p>
        )}

        {!data.receiptsAvailable ? (
          <p role="alert" style={{ color: 'var(--status-warn-fg)' }} data-testid="cash-receipt-unavailable">
            {t('orders.cashReceipt.receiptsUnavailable', '발급 이력을 불러오지 못했습니다. 잠시 후 다시 확인해 주세요.')}
          </p>
        ) : data.receipts.length > 0 ? (
          <Table data-testid="cash-receipt-items">
            <TableHeader>
              <TableRow>
                <TableHead>{t('orders.cashReceipt.col.source', '구분')}</TableHead>
                <TableHead>{t('orders.cashReceipt.col.status', '상태')}</TableHead>
                <TableHead>{t('orders.cashReceipt.col.amount', '발급액')}</TableHead>
                <TableHead>{t('orders.cashReceipt.col.canceled', '취소액')}</TableHead>
                <TableHead>{t('orders.cashReceipt.col.remaining', '잔액')}</TableHead>
                <TableHead>{t('orders.cashReceipt.col.approval', '승인번호')}</TableHead>
                <TableHead>{t('orders.cashReceipt.col.issuedAt', '발급일시')}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.receipts.map((r: CashReceiptItem) => (
                <TableRow key={r.cashReceiptId} data-testid="cash-receipt-item">
                  <TableCell>{receiptSourceLabel(r.source)}</TableCell>
                  <TableCell>
                    <Badge size="sm" variant={statusVariant(r.status)}>{statusLabel(r.status)}</Badge>
                    {r.status === 'FAILED' && r.failureMessage ? (
                      <div className="mt-1 text-[11px]" style={{ color: 'var(--status-danger-fg)' }}>{r.failureMessage}</div>
                    ) : null}
                  </TableCell>
                  <TableCell className="tabular-nums">{won(r.amount)}</TableCell>
                  <TableCell className="tabular-nums">{r.canceledAmount > 0 ? `- ${won(r.canceledAmount)}` : won(0)}</TableCell>
                  <TableCell className="tabular-nums">{won(r.remainingAmount)}</TableCell>
                  <TableCell className="tabular-nums">{r.issueNumber ?? r.legacyApprovalNo ?? '-'}</TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums">{formatDate(r.issuedAt ?? r.requestedAt)}</TableCell>
                  <TableCell>
                    {r.receiptUrl ? (
                      <a href={r.receiptUrl} target="_blank" rel="noopener noreferrer" className="text-[color:var(--accent-default)] underline">
                        {t('orders.cashReceipt.view', '영수증 보기')}
                      </a>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="text-[color:var(--content-muted)]">{t('orders.cashReceipt.noReceipts', '발급 이력이 없습니다.')}</p>
        )}

        {canWrite ? (
          <div className="flex flex-wrap gap-2">
            {canReissue ? (
              <Button size="sm" variant="primary" disabled={busy} onClick={() => runIssue(null)} data-testid="cash-receipt-reissue">
                {t('orders.cashReceipt.reissue', '재발급')}
              </Button>
            ) : null}
            {data.eligible ? (
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => {
                  setFormError(null);
                  setForm({
                    receiptType: (req?.receiptType as CashReceiptType) === 'EXPENSE_PROOF' ? 'EXPENSE_PROOF' : 'INCOME_DEDUCTION',
                    identityType: (req?.identityType as CashReceiptIdentityType) === 'BUSINESS_NO' ? 'BUSINESS_NO' : 'PHONE',
                    identityNumber: '',
                  });
                  setIssueOpen(true);
                }}
                data-testid="cash-receipt-issue-open"
              >
                {req ? t('orders.cashReceipt.changeAndIssue', '정보 변경 후 발급') : t('orders.cashReceipt.issueNew', '발급 신청')}
              </Button>
            ) : null}
            {cancellable ? (
              <Button
                size="sm"
                variant="danger"
                disabled={busy}
                onClick={() => { setCancelError(null); setCancelAmount(''); setCancelReason(''); setCancelOpen(true); }}
                data-testid="cash-receipt-cancel-open"
              >
                {t('orders.cashReceipt.cancel', '발급 취소')}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* 정보 변경 후 발급 — 신청을 이 값으로 바꾸고 발급(입금 전이면 신청만 저장, 입금확인 시 자동 발급). */}
      <Dialog open={issueOpen} onOpenChange={(o) => !o && setIssueOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('orders.cashReceipt.issueTitle', '현금영수증 발급')}</DialogTitle>
            <DialogDescription>
              {t('orders.cashReceipt.issueHint', '신청 정보를 이 값으로 바꾸고 발급합니다. 입금확인 전이면 신청만 저장되고 입금확인 시 자동 발급됩니다.')}
            </DialogDescription>
          </DialogHeader>
          <form className="flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); submitIssue(); }}>
            <label className="flex flex-col gap-1 text-sm">
              {t('orders.cashReceipt.receiptType', '발급 유형')}
              <Select
                value={form.receiptType}
                onChange={(e) => {
                  const receiptType = e.target.value as CashReceiptType;
                  setForm((f) => ({ ...f, receiptType, identityType: receiptType === 'INCOME_DEDUCTION' ? 'PHONE' : f.identityType }));
                }}
                data-testid="cash-receipt-type"
              >
                <option value="INCOME_DEDUCTION">{typeLabel('INCOME_DEDUCTION')}</option>
                <option value="EXPENSE_PROOF">{typeLabel('EXPENSE_PROOF')}</option>
              </Select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              {t('orders.cashReceipt.identityTypeLabel', '발급 번호 종류')}
              <Select
                value={form.identityType}
                onChange={(e) => setForm((f) => ({ ...f, identityType: e.target.value as CashReceiptIdentityType }))}
                disabled={form.receiptType === 'INCOME_DEDUCTION'}
                data-testid="cash-receipt-identity-type"
              >
                <option value="PHONE">{identityLabel('PHONE')}</option>
                <option value="BUSINESS_NO">{identityLabel('BUSINESS_NO')}</option>
              </Select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              {identityLabel(form.identityType)}
              <Input
                value={form.identityNumber}
                onChange={(e) => setForm((f) => ({ ...f, identityNumber: e.target.value }))}
                placeholder={form.identityType === 'PHONE' ? '010-1234-5678' : '123-45-67890'}
                inputMode="numeric"
                maxLength={20}
                data-testid="cash-receipt-identity-number"
              />
            </label>
            {formError ? <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>{formError}</p> : null}
            <DialogFooter>
              <Button type="button" size="sm" variant="ghost" onClick={() => setIssueOpen(false)} disabled={issue.isPending}>
                {t('orders.cashReceipt.close', '닫기')}
              </Button>
              <Button type="submit" size="sm" variant="primary" disabled={issue.isPending} data-testid="cash-receipt-issue-submit">
                {t('orders.cashReceipt.issueSubmit', '발급')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* 취소 — 금액을 비우면 잔액 전부, 넣으면 부분 취소. */}
      <Dialog open={cancelOpen} onOpenChange={(o) => !o && setCancelOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('orders.cashReceipt.cancelTitle', '현금영수증 취소')}</DialogTitle>
            <DialogDescription>
              {cancellable
                ? t('orders.cashReceipt.cancelHint', {
                    defaultValue: '금액을 비우면 잔액 {{remaining}} 전부를 취소합니다. 금액을 넣으면 부분 취소합니다.',
                    remaining: won(cancellable.remainingAmount),
                  })
                : null}
            </DialogDescription>
          </DialogHeader>
          <form className="flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); submitCancel(); }}>
            <label className="flex flex-col gap-1 text-sm">
              {t('orders.cashReceipt.cancelAmount', '취소 금액 (선택)')}
              <Input
                value={cancelAmount}
                onChange={(e) => setCancelAmount(e.target.value)}
                inputMode="numeric"
                placeholder={t('orders.cashReceipt.cancelAll', '비우면 잔액 전부')}
                data-testid="cash-receipt-cancel-amount"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              {t('orders.cashReceipt.cancelReason', '취소 사유 (선택)')}
              <Input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} maxLength={200} data-testid="cash-receipt-cancel-reason" />
            </label>
            {cancelError ? <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>{cancelError}</p> : null}
            <DialogFooter>
              <Button type="button" size="sm" variant="ghost" onClick={() => setCancelOpen(false)} disabled={cancel.isPending}>
                {t('orders.cashReceipt.close', '닫기')}
              </Button>
              <Button type="submit" size="sm" variant="danger" disabled={cancel.isPending} data-testid="cash-receipt-cancel-submit">
                {t('orders.cashReceipt.cancelSubmit', '취소하기')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
};
